import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { validateUserForm } from '../Validations/ValidationsUser';

export default function UserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    nombreCompleto: '',
    usuario: '',
    email: '',
    password: '',
    role: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const fetchUser = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (err) {
      setServerError('Error al cargar el usuario. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser(prevUser => ({
      ...prevUser,
      [name]: value
    }));
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setErrors({});

    const validationErrors = await validateUserForm(user, !id);
    if (Object.keys(validationErrors).length === 0) {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (id) {
          await axios.put(`/users/${id}`, user, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post('/auth/register', user, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        navigate('/home/usuarios');
      } catch (err) {
        console.error('Error saving user:', err);
        setServerError(err.response?.data?.message || 'Error al guardar el usuario. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-semibold mb-6">{id ? 'Editar Usuario' : 'Añadir Nuevo Usuario'}</h2>
      {serverError && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{serverError}</div>}
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nombreCompleto">
            Nombre Completo
          </label>
          <input
            type="text"
            name="nombreCompleto"
            value={user.nombreCompleto}
            onChange={handleChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.nombreCompleto ? 'border-red-500' : ''}`}
            placeholder="Nombre Completo"
          />
          {errors.nombreCompleto && <p className="text-red-500 text-xs italic">{errors.nombreCompleto}</p>}
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="usuario">
            Usuario
          </label>
          <input
            type="text"
            name="usuario"
            value={user.usuario}
            onChange={handleChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.usuario ? 'border-red-500' : ''}`}
            placeholder="Usuario"
          />
          {errors.usuario && <p className="text-red-500 text-xs italic">{errors.usuario}</p>}
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={user.email}
            onChange={handleChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.email ? 'border-red-500' : ''}`}
            placeholder="Email"
          />
          {errors.email && <p className="text-red-500 text-xs italic">{errors.email}</p>}
        </div>
        {!id && (
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              Contraseña
            </label>
            <input
              type="password"
              name="password"
              value={user.password}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.password ? 'border-red-500' : ''}`}
              placeholder="Contraseña"
            />
            {errors.password && <p className="text-red-500 text-xs italic">{errors.password}</p>}
          </div>
        )}
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">
            Rol
          </label>
          <select
            name="role"
            value={user.role}
            onChange={handleChange}
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.role ? 'border-red-500' : ''}`}
          >
            <option value="">Seleccione un rol</option>
            <option value="user">Usuario</option>
            <option value="admin">Administrador</option>
            <option value="userManagement">Gestión de Usuarios</option>
          </select>
          {errors.role && <p className="text-red-500 text-xs italic">{errors.role}</p>}
        </div>
        <div className="flex items-center justify-between">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : (id ? 'Actualizar Usuario' : 'Crear Usuario')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/home/usuarios')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}